import { useEffect, useRef, useState } from 'react'

import { api } from '../api/client'
import type { RoutePath } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { RoutePathGraph } from '../components/RoutePathGraph'
import { t } from '../i18n/lingua'

/** Esito → (testo, classe del badge). */
function esito(outcome: string): { testo: string; cls: string } {
  switch (outcome) {
    case 'reached':
      return { testo: t('Destinazione raggiunta'), cls: 'badge--ok' }
    case 'exits':
      return { testo: t('Esce verso l’esterno'), cls: '' }
    case 'same_subnet':
      return { testo: t('Stessa rete'), cls: 'badge--ok' }
    case 'no_route':
      return { testo: t('Nessuna rotta'), cls: 'badge--warn' }
    case 'no_table':
      return { testo: t('Tabella di routing mancante'), cls: 'badge--warn' }
    case 'loop':
      return { testo: t('Anello di routing'), cls: 'badge--warn' }
    case 'source_unknown':
      return { testo: t('Sorgente sconosciuta'), cls: 'badge--warn' }
    case 'no_data':
      return { testo: t('Nessun dato'), cls: 'badge--warn' }
    default:
      return { testo: outcome, cls: 'badge--warn' }
  }
}

/**
 * Analizzatore di percorso L3 (Traffico → Routing): IP sorgente + destinazione →
 * il **giro** che il pacchetto farebbe attraverso le tabelle di routing raccolte,
 * salto per salto, con una piccola animazione. Non emula nulla: ricostruisce il
 * percorso dai dati in CMDB.
 */
export default function RouteAnalyzer() {
  const [src, setSrc] = useState('')
  const [dst, setDst] = useState('')
  const [path, setPath] = useState<RoutePath | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [visibili, setVisibili] = useState(0)
  const timers = useRef<number[]>([])

  // Rivela i salti uno alla volta (animazione): a ogni nuovo percorso riparte.
  useEffect(() => {
    timers.current.forEach(clearTimeout)
    timers.current = []
    if (!path) return
    setVisibili(0)
    // Un passo in più degli hop: l'ultimo rivela il nodo finale del grafo.
    for (let i = 1; i <= path.hops.length + 1; i++) {
      timers.current.push(window.setTimeout(() => setVisibili(i), 350 * i))
    }
    return () => timers.current.forEach(clearTimeout)
  }, [path])

  async function analizza() {
    setBusy(true)
    setError(null)
    try {
      setPath(await api.routeAnalyze(src.trim(), dst.trim()))
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  function riproduci() {
    if (!path) return
    setVisibili(0)
    timers.current.forEach(clearTimeout)
    timers.current = Array.from({ length: path.hops.length + 1 }, (_, i) =>
      window.setTimeout(() => setVisibili(i + 1), 350 * (i + 1)),
    )
  }

  const stato = path ? esito(path.outcome) : null

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Routing')}</h1>
          <p className="page__subtitle">
            {t('ricostruisce il percorso che un pacchetto effettua tra un IP sorgente e uno di destinazione, utilizzando le tabelle di routing acquisite dagli asset di rete.')}
          </p>
        </div>
      </header>

      <Card title={t('Analizzatore di percorso')}>
        <p className="field__hint">
          {t('Ricostruzione del percorso di routing: calcola il percorso previsto tra sorgente e destinazione dalle tabelle di routing presenti nell’inventario. Riflette l’ultima scansione, non il traffico in tempo reale, e non considera ECMP, policy-based routing, VRF o NAT.')}
        </p>
        <div className="ci-sommario" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field" style={{ margin: 0 }}>
            <span className="field__label">{t('IP sorgente')}</span>
            <input value={src} onChange={(e) => setSrc(e.target.value)} placeholder="10.0.0.50" />
          </label>
          <label className="field" style={{ margin: 0 }}>
            <span className="field__label">{t('IP destinazione')}</span>
            <input value={dst} onChange={(e) => setDst(e.target.value)} placeholder="203.0.113.5" />
          </label>
          <button onClick={analizza} disabled={busy || !src.trim() || !dst.trim()}>
            {busy ? t('Analizzo…') : t('Analizza')}
          </button>
          {path && path.hops.length > 0 && (
            <button className="secondary" onClick={riproduci}>
              {t('Riproduci')}
            </button>
          )}
        </div>

        <ErrorBanner error={error} />

        {busy && <Spinner label={t('Analizzo il percorso… (aggiorno le rotte dei firewall)')} />}

        {!busy && path && (
          <div style={{ marginTop: 16 }}>
            <p>
              <span className="mono">{path.src}</span> → <span className="mono">{path.dst}</span>{' '}
              {stato && <span className={`badge ${stato.cls}`}>{stato.testo}</span>}
            </p>
            {path.reason && <p className="field__hint">{path.reason}</p>}

            {path.hops.length === 0 ? (
              <EmptyState title={t('Nessun salto da mostrare')} hint={path.reason ?? ''} />
            ) : (
              <>
              <RoutePathGraph path={path} visibili={visibili} />
              <ol className="route-path" style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
                {path.hops.map((h, i) => (
                  <li
                    key={`${h.ci_id}-${i}`}
                    style={{
                      opacity: i < visibili ? 1 : 0.15,
                      transform: i < visibili ? 'translateX(0)' : 'translateX(-6px)',
                      transition: 'opacity .25s, transform .25s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '8px 0',
                    }}
                  >
                    <span
                      className="badge"
                      style={{ minWidth: 22, textAlign: 'center' }}
                      aria-hidden
                    >
                      {i + 1}
                    </span>
                    <div style={{ flex: 1 }}>
                      <strong>{h.ci_name}</strong>
                      <div className="field__hint" style={{ margin: 0 }}>
                        {h.matched ? (
                          <>
                            {t('rotta')} <span className="mono">{h.matched}</span>
                            {h.interface ? <> · {t('uscita')} <span className="mono">{h.interface}</span></> : null}
                            {h.proto ? <> · {h.proto}</> : null}
                          </>
                        ) : (
                          t('tabella di routing non raccolta: il percorso si conosce fin qui')
                        )}
                      </div>
                      {h.note && h.matched && (
                        <div className="field__hint" style={{ margin: 0, color: 'var(--warn)' }}>
                          {h.note}
                        </div>
                      )}
                    </div>
                    {h.next_hop ? (
                      <span className="mono" title={t('Next hop')}>
                        → {h.next_hop}
                      </span>
                    ) : h.matched ? (
                      <span className="badge badge--ok">{t('connessa')}</span>
                    ) : (
                      <span className="badge badge--warn">?</span>
                    )}
                  </li>
                ))}
              </ol>
              </>
            )}
          </div>
        )}
      </Card>
    </>
  )
}
